import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { ProjectForm } from '../components/ProjectForm';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Calendar,
  User,
  FolderKanban,
  List,
  LayoutGrid,
  ChevronRight,
  Eye
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format, parseISO } from 'date-fns';

const STATUS_CONFIG = {
  planning: { label: 'Planning', color: 'bg-slate-100 text-slate-700' },
  active: { label: 'Active', color: 'bg-blue-50 text-blue-700' },
  on_hold: { label: 'On Hold', color: 'bg-amber-50 text-amber-700' },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Medium', color: 'bg-blue-50 text-blue-600' },
  high: { label: 'High', color: 'bg-amber-50 text-amber-600' },
  critical: { label: 'Critical', color: 'bg-red-50 text-red-600' }
};

const ProjectCard = ({ project, users, onEdit, onDelete, onView }) => {
  const owner = users.find(u => u.id === project.owner_id);
  
  return (
    <Card 
      className="mcc-card hover:shadow-md transition-all cursor-pointer" 
      data-testid={`project-card-${project.id}`}
      onClick={() => onView(project.id)}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <Badge className={STATUS_CONFIG[project.status]?.color || 'bg-slate-100'}>
            {STATUS_CONFIG[project.status]?.label || project.status}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onView(project.id); }}>
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(project); }}>Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(project.id); }} className="text-red-600">Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1">{project.name}</h3>
        <p className="text-sm text-slate-500 mb-2 line-clamp-2">{project.description || 'No description'}</p>
        
        {project.campaign_type && (
          <Badge variant="outline" className="mb-3 text-xs bg-blue-50 text-blue-700 border-blue-200">
            {project.campaign_type}
          </Badge>
        )}
        
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <User className="w-3.5 h-3.5" />
            <span>{owner?.name || 'Unassigned'}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            <span>{project.launch_date ? format(parseISO(project.launch_date), 'MMM d') : (project.end_date ? format(parseISO(project.end_date), 'MMM d') : 'No deadline')}</span>
          </div>
        </div>
        
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
          <Badge variant="outline" className={PRIORITY_CONFIG[project.priority]?.color}>
            {PRIORITY_CONFIG[project.priority]?.label || project.priority}
          </Badge>
          {project.department && (
            <span className="text-xs text-slate-400">{project.department}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const KanbanColumn = ({ status, projects, users, onEdit, onDelete, onView, onDragOver, onDrop }) => {
  const statusConfig = STATUS_CONFIG[status];
  const columnProjects = projects.filter(p => p.status === status);
  
  return (
    <div 
      className="kanban-column"
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, status)}
      data-testid={`kanban-column-${status}`}
    >
      <div className="kanban-column-header">
        <span className="kanban-column-title">{statusConfig?.label || status}</span>
        <span className="kanban-column-count">{columnProjects.length}</span>
      </div>
      <ScrollArea className="flex-1">
        <div className="space-y-3 pb-2">
          {columnProjects.map(project => (
            <div
              key={project.id}
              draggable
              onDragStart={(e) => e.dataTransfer.setData('projectId', project.id)}
              onClick={() => onView(project.id)}
              className="kanban-card cursor-pointer"
              data-testid={`kanban-card-${project.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <Badge className={PRIORITY_CONFIG[project.priority]?.color} variant="outline">
                  {PRIORITY_CONFIG[project.priority]?.label}
                </Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                      <MoreVertical className="w-3.5 h-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onView(project.id); }}>
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(project); }}>Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(project.id); }} className="text-red-600">Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <h4 className="font-medium text-sm text-slate-900 mb-1">{project.name}</h4>
              <p className="text-xs text-slate-500 line-clamp-2 mb-3">{project.description}</p>
              {project.campaign_type && (
                <Badge variant="outline" className="mb-2 text-xs bg-blue-50 text-blue-700 border-blue-200">
                  {project.campaign_type}
                </Badge>
              )}
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>{users.find(u => u.id === project.owner_id)?.name || 'Unassigned'}</span>
                <span>{project.launch_date ? format(parseISO(project.launch_date), 'MMM d') : (project.end_date ? format(parseISO(project.end_date), 'MMM d') : '-')}</span>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export const ProjectsPage = () => {
  const { api, user } = useAuth();
  const { subscribe } = useWebSocket();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status');
  
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(urlStatus || 'all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, usersRes] = await Promise.all([
        api.get('/projects'),
        api.get('/users')
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time updates for projects
  useEffect(() => {
    const unsubscribes = [];
    
    unsubscribes.push(subscribe('project_created', (message) => {
      if (message.data) {
        setProjects(prev => {
          if (prev.some(p => p.id === message.data.id)) return prev;
          return [...prev, message.data];
        });
      }
    }));
    
    unsubscribes.push(subscribe('project_updated', (message) => {
      if (message.data) {
        setProjects(prev => prev.map(p => 
          p.id === message.data.id ? { ...p, ...message.data } : p
        ));
      }
    }));
    
    unsubscribes.push(subscribe('project_deleted', (message) => {
      if (message.data?.id) {
        setProjects(prev => prev.filter(p => p.id !== message.data.id));
      }
    }));

    return () => unsubscribes.forEach(unsub => unsub());
  }, [subscribe]);

  const handleSubmit = async (data) => {
    if (editingProject) {
      await api.put(`/projects/${editingProject.id}`, data);
    } else {
      const response = await api.post('/projects', { ...data, owner_id: data.owner_id || user.id });
      // Immediately add the new project to the list
      if (response.data) {
        setProjects(prev => {
          if (prev.some(p => p.id === response.data.id)) return prev;
          return [response.data, ...prev];
        });
      }
    }
    setDialogOpen(false);
    setEditingProject(null);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setDialogOpen(true);
  };

  const handleView = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await api.delete(`/projects/${projectId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  const handleDrop = async (e, newStatus) => {
    const projectId = e.dataTransfer.getData('projectId');
    try {
      await api.put(`/projects/${projectId}`, { status: newStatus });
    } catch (error) {
      console.error('Failed to update project status:', error);
    }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Projects</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">{projects.length} total projects</p>
        </div>
        
        {can('project:create') && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingProject(null); }}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="new-project-btn">
                <Plus className="w-4 h-4" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
              <DialogHeader>
                <DialogTitle>{editingProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
              </DialogHeader>
              <ProjectForm
                initialData={editingProject}
                users={users}
                onSubmit={handleSubmit}
                onCancel={() => { setDialogOpen(false); setEditingProject(null); }}
                isEditing={!!editingProject}
                currentUserId={user?.id}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-64"
              data-testid="search-projects"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          <Button
            variant={view === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('grid')}
            data-testid="view-grid"
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
          <Button
            variant={view === 'kanban' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('kanban')}
            data-testid="view-kanban"
          >
            <FolderKanban className="w-4 h-4" />
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('list')}
            data-testid="view-list"
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {view === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No projects found</h3>
              <p className="text-slate-500">Create your first project to get started</p>
            </div>
          ) : (
            filteredProjects.map(project => (
              <ProjectCard
                key={project.id}
                project={project}
                users={users}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            ))
          )}
        </div>
      )}

      {view === 'kanban' && (
        <div className="kanban-board">
          {Object.keys(STATUS_CONFIG).map(status => (
            <KanbanColumn
              key={status}
              status={status}
              projects={filteredProjects}
              users={users}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            />
          ))}
        </div>
      )}

      {view === 'list' && (
        <Card className="mcc-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left p-4 font-medium text-slate-500 text-sm">Name</th>
                  <th className="text-left p-4 font-medium text-slate-500 text-sm">Campaign Type</th>
                  <th className="text-left p-4 font-medium text-slate-500 text-sm">Status</th>
                  <th className="text-left p-4 font-medium text-slate-500 text-sm">Priority</th>
                  <th className="text-left p-4 font-medium text-slate-500 text-sm">Owner</th>
                  <th className="text-left p-4 font-medium text-slate-500 text-sm">Launch Date</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map(project => (
                  <tr 
                    key={project.id} 
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" 
                    data-testid={`project-row-${project.id}`}
                    onClick={() => handleView(project.id)}
                  >
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-slate-900">{project.name}</p>
                        <p className="text-sm text-slate-500 line-clamp-1">{project.description}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      {project.campaign_type && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          {project.campaign_type}
                        </Badge>
                      )}
                    </td>
                    <td className="p-4">
                      <Badge className={STATUS_CONFIG[project.status]?.color}>
                        {STATUS_CONFIG[project.status]?.label}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className={PRIORITY_CONFIG[project.priority]?.color}>
                        {PRIORITY_CONFIG[project.priority]?.label}
                      </Badge>
                    </td>
                    <td className="p-4 text-sm text-slate-600">
                      {users.find(u => u.id === project.owner_id)?.name || '-'}
                    </td>
                    <td className="p-4 text-sm text-slate-600">
                      {project.launch_date ? format(parseISO(project.launch_date), 'MMM d, yyyy') : (project.end_date ? format(parseISO(project.end_date), 'MMM d, yyyy') : '-')}
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleView(project.id)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(project)}>Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(project.id)} className="text-red-600">Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
